/**
 * What to tell a creator about an uploaded file — truthfully.
 *
 * A stored source file and a previewable file are different things. A successful upload of a format DoAR cannot
 * preview yet is NOT a failure and is NOT "converting": the original is safe, preview just isn't available for
 * that format. Only formats with a real converter (video → MP4) ever say "converting".
 *
 * The backend supplies `asset.capability` ({ capability, sourceStored, previewAvailable, arAvailable, arKind,
 * conversion: { status, reason } }). When it is absent (older responses) the same answer is derived from the asset's
 * status and extension, and a stale uploaded/processing state on a non-video file is never shown as "converting".
 */

import { getCapability } from './fileTypeMap';

const THREE_D_HINT_CATEGORIES = new Set(['model3d', 'cad_bim', 'pointcloud']);

function extensionOf(asset) {
  const raw = String(asset?.extension || '').toLowerCase();
  return raw && !raw.startsWith('.') ? `.${raw}` : raw;
}

/** Normalizes backend capability (or derives it) into { capability, converting, failedConversion, sourceOnly }. */
export function readAssetState(asset) {
  const cap = asset?.capability || null;
  const capability = cap?.capability || getCapability(extensionOf(asset));
  const status = asset?.status;
  const isVideo = asset?.category === 'video';
  const conversionStatus = cap?.conversion?.status || null;

  const sourceOnly = capability === 'SOURCE_ONLY' || status === 'unsupported'
    // Legacy scaffold leftovers: a non-video file stuck "uploaded/processing" has no converter behind it.
    || (!isVideo && ['uploaded', 'processing'].includes(status) && capability !== 'DIRECT');
  const converting = !sourceOnly && isVideo && (conversionStatus === 'processing' || (!conversionStatus && status === 'processing'));
  const failedConversion = !sourceOnly && ((conversionStatus === 'failed') || (isVideo && status === 'failed'));

  return {
    capability,
    sourceOnly,
    converting,
    failedConversion,
    previewAvailable: Boolean(cap?.previewAvailable),
    arKind: cap?.arKind || null,
    processingDisabled: conversionStatus === 'unavailable' && cap?.conversion?.reason === 'processing_disabled',
  };
}

/** Success panel shown right after an upload completes. */
export function describeUploadOutcome(asset) {
  const s = readAssetState(asset);
  const name = asset?.originalName || 'Your file';
  if (s.sourceOnly) {
    const hint = THREE_D_HINT_CATEGORIES.has(asset?.category)
      ? ' To preview it in 3D, export or upload a GLB alongside it.'
      : '';
    return {
      kind: 'source_saved',
      icon: '✓',
      title: 'Source file saved',
      sub: `Preview isn't available for this format yet. Your original is stored safely in the project.${hint}`,
    };
  }
  if (s.converting) {
    return { kind: 'converting', icon: '⬆', title: 'Uploaded — converting', sub: 'Your video is being converted to MP4 for playback. You can keep working while it finishes.' };
  }
  if (s.failedConversion) {
    return { kind: 'conversion_failed', icon: '✓', title: 'Source file saved', sub: "Your original is stored safely, but converting it for playback didn't finish. You can retry from the asset library." };
  }
  if (s.processingDisabled) {
    return { kind: 'source_saved', icon: '✓', title: 'Source file saved', sub: "Preview isn't available for this video format right now. Your original is stored safely in the project." };
  }
  if (s.arKind === 'quicklook') {
    return { kind: 'ready', icon: '✓', title: 'Upload Complete', sub: `${name} is ready as an AR delivery file (opens in iPhone Quick Look).` };
  }
  return { kind: 'ready', icon: '✓', title: 'Upload Complete', sub: `${name} is ready.` };
}

/** Notice shown BEFORE upload, from the file's extension. Returns null when nothing needs saying. */
export function describePreUpload(fileInfo) {
  const capability = fileInfo?.capability;
  if (capability === 'REJECTED' || !fileInfo) {
    return { kind: 'unsupported', title: 'Unsupported file type', sub: 'This file type cannot be uploaded. Choose a supported format.' };
  }
  if (capability === 'SOURCE_ONLY') {
    return { kind: 'source_only', title: 'Preview not available for this format yet', sub: 'The original will be saved to your project safely. DoAR cannot preview or convert this format yet.' };
  }
  if (capability === 'REAL_CONVERSION') {
    return { kind: 'converting', title: 'Will be converted for playback', sub: 'This video is converted to MP4 after upload. You can keep working while it finishes.' };
  }
  return null;
}

/** Asset-library status badge label. `unsupported` means "source saved", never a failure. */
export function describeAssetStatus(asset) {
  const s = readAssetState(asset);
  if (s.sourceOnly) return { label: 'Source saved', tone: 'neutral', title: "Stored safely. Preview isn't available for this format yet." };
  switch (asset?.status) {
    case 'ready': return { label: 'Ready', tone: 'ok', title: '' };
    case 'uploading': return { label: 'Uploading', tone: 'info', title: '' };
    case 'processing': return { label: s.converting ? 'Converting' : 'Processing', tone: 'warn', title: '' };
    case 'failed': return s.failedConversion
      ? { label: 'Conversion failed', tone: 'warn', title: 'The original is stored safely. You can retry the conversion.' }
      : { label: 'Failed', tone: 'error', title: '' };
    case 'uploaded': return { label: 'Stored', tone: 'neutral', title: '' };
    default: return { label: asset?.status || '', tone: 'neutral', title: '' };
  }
}

/** A conversion can only be (re)attempted where a real converter exists: a failed video conversion. */
export function canRetryConversion(asset) {
  return asset?.category === 'video' && asset?.status === 'failed';
}

/** Body copy for a format the inline viewer cannot show. Never promises a conversion. */
export function describeViewerFallback(ext) {
  const label = ext ? ext.replace(/^\./, '').toUpperCase() : 'This';
  return {
    line1: `${label} format can't be previewed here yet.`,
    line2: "The source file is stored safely. Export or upload a GLB to preview it in 3D.",
  };
}
